/**
 * Scene browser for embed-frame targets (Block C.3). Derived from MoveDialog —
 * the same guarded folder-tree picker, but it SHOWS files (not just folders),
 * restricts selection to `kind = board`, and forbids self-reference (the current
 * scene never appears as a choice — same failure class as the folder cycle in
 * v1.1 Block 3). No new endpoint, no new guard path: the listing is already
 * guarded, so only readable boards appear.
 */

import { useCallback, useEffect, useState } from "react";

import { ryfeApi } from "../api";

import "./ryfe.scss";

import type { FolderChild, FolderListing } from "../api";

export const SceneBrowserDialog = ({
  wsId,
  currentFileId,
  onPick,
  onClose,
  // CC_05 Block 6 — reused for element-linking: pick from more than boards
  // (a note or a list too). Default stays board-only for the embed-target use.
  kinds = ["board"],
  title = "Ziel-Szene wählen",
}: {
  wsId: string;
  currentFileId: string;
  onPick: (fileId: string, name: string) => void;
  onClose: () => void;
  kinds?: string[];
  title?: string;
}) => {
  const [listing, setListing] = useState<FolderListing | null>(null);
  const [stack, setStack] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const openRoot = useCallback(async () => {
    const root = await ryfeApi.getRoot(wsId);
    setListing(root);
    setStack([{ id: root.id, name: "Home" }]);
  }, [wsId]);

  useEffect(() => {
    openRoot().catch((e) => setError((e as Error).message));
  }, [openRoot]);

  const enter = async (f: FolderChild) => {
    try {
      const l = await ryfeApi.listFolder(wsId, f.id);
      setListing(l);
      setStack((s) => [...s, { id: f.id, name: f.name }]);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const up = async (depth: number) => {
    try {
      const l =
        depth === 0
          ? await ryfeApi.getRoot(wsId)
          : await ryfeApi.listFolder(wsId, stack[depth].id);
      setListing(l);
      setStack((s) => s.slice(0, depth + 1));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const boards = (listing?.files || []).filter((f) => kinds.includes(f.kind));

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">{title}</h2>

        <nav className="ryfe-breadcrumb">
          {stack.map((c, i) => (
            <span key={c.id}>
              {i > 0 && " / "}
              <button className="ryfe-btn ryfe-btn--link" onClick={() => up(i)}>
                {c.name}
              </button>
            </span>
          ))}
        </nav>

        <div className="ryfe-picker">
          {!listing ? (
            <div className="ryfe-muted">Loading…</div>
          ) : (
            <>
              {listing.folders.map((f) => (
                <button
                  key={f.id}
                  className="ryfe-picker__row"
                  onClick={() => enter(f)}
                >
                  📁 {f.name}
                </button>
              ))}
              {boards.map((f) => (
                <button
                  key={f.id}
                  className="ryfe-picker__row"
                  disabled={f.id === currentFileId}
                  onClick={() => onPick(f.id, f.name)}
                  title={
                    f.id === currentFileId ? "Das ist die aktuelle Szene" : ""
                  }
                >
                  🗒 {f.name}
                </button>
              ))}
              {listing.folders.length === 0 && boards.length === 0 && (
                <div className="ryfe-muted">Nichts Passendes hier.</div>
              )}
            </>
          )}
        </div>

        {error && <div className="ryfe-auth__error">{error}</div>}

        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};
